
import React from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import html2canvas from 'html2canvas';

interface FigmaExportButtonProps {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}

const FigmaExportButton = ({ variant = "outline", size = "sm" }: FigmaExportButtonProps) => {
  const { toast } = useToast();

  const handleExportToFigma = async () => {
    try {
      // Show loading toast
      toast({
        title: "Capturing screenshot...",
        description: "Please wait while we capture the current page.",
      });

      // Wait a bit for the toast to render, then capture
      setTimeout(async () => {
        try {
          const canvas = await html2canvas(document.body, {
            height: window.innerHeight,
            width: window.innerWidth,
            scrollX: 0,
            scrollY: 0,
            useCORS: true,
            allowTaint: true,
          });

          // Convert to blob
          canvas.toBlob(async (blob) => {
            if (blob) {
              try {
                // Try to copy to clipboard first
                await navigator.clipboard.write([
                  new ClipboardItem({ 'image/png': blob })
                ]);
                
                toast({
                  title: "Copied to clipboard!",
                  description: "Screenshot copied to clipboard. You can now paste it directly into Figma.",
                });
              } catch (clipboardError) {
                // Fallback to download if clipboard fails
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `page-export-${Date.now()}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                toast({
                  title: "Downloaded!",
                  description: "Screenshot downloaded. You can now import it into Figma.",
                });
              }
            }
          }, 'image/png');
        } catch (error) {
          console.error('Screenshot capture failed:', error);
          toast({
            title: "Export failed",
            description: "Unable to capture screenshot. Please try again.",
            variant: "destructive",
          });
        }
      }, 500);
    } catch (error) {
      console.error('Export to Figma failed:', error);
      toast({
        title: "Export failed",
        description: "Unable to export to Figma. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleExportToFigma}
      className="flex items-center gap-2"
    >
      <svg
        className="w-4 h-4"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 12h5.5a5.5 5.5 0 1 0 0-11H12v11z"
          fill="currentColor"
        />
        <path
          d="M6.5 1A5.5 5.5 0 0 0 1 6.5v11A5.5 5.5 0 0 0 6.5 23H12V12H6.5A5.5 5.5 0 0 1 1 6.5A5.5 5.5 0 0 1 6.5 1z"
          fill="currentColor"
        />
      </svg>
      Figma
    </Button>
  );
};

export default FigmaExportButton;
